/**
 * Token name constants — single source of truth for runtime theming.
 *
 * These arrays MUST stay in sync with the SCSS maps in:
 *   - radiant-styles/css/foundations/_tokens-system.scss   ($rd-sys-color-map)
 *   - radiant-styles/css/foundations/_tokens-component.scss ($rd-comp-color-map)
 *
 * When a token is added/removed in SCSS, update this file too.
 */

// =========================================================================
// System token names (43 tokens)
// Must match keys in $rd-sys-color-map
// =========================================================================

export const SYSTEM_TOKEN_NAMES = [
    // Background (17)
    'background-sunken',
    'background-base',
    'background-on-base',
    'background-raised',
    'background-active',
    'background-action',
    'background-brand',
    'background-overlay',
    'background-base-inverse',
    'background-raised-inverse',
    'background-success',
    'background-warning',
    'background-failure',
    'background-information',
    'background-ghost-hover',
    'background-ghost-highlight',

    // Content (15)
    'content-primary',
    'content-secondary',
    'content-tertiary',
    'content-alternate',
    'content-brand',
    'content-primary-inverse',
    'content-alternate-inverse',
    'content-brand-inverse',
    'content-success',
    'content-warning',
    'content-failure',
    'content-information',
    'content-link',
    'content-link-inverse',
    'content-link-inverse-hover',

    // Border (12)
    'border-default',
    'border-hover',
    'border-divider',
    'border-brand',
    'border-warning',
    'border-failure',
    'border-default-inverse',
    'border-hover-inverse',
    'border-divider-inverse',
    'border-brand-inverse',
    'border-focus',
    'border-gap',
] as const;

// =========================================================================
// Component token names (must match keys in $rd-comp-color-map)
// =========================================================================

export const COMPONENT_TOKEN_NAMES = [
    // Button
    'button-primary-default',
    'button-primary-hover',
    'button-primary-pressed',
    'button-primary-on-color-default',
    'button-primary-on-color-hover',
    'button-primary-on-color-pressed',
    'button-secondary-default',
    'button-secondary-hover',
    'button-secondary-pressed',
    'button-tertiary-hover',
    'button-tertiary-pressed',

    // Chip – Loading
    'chip-loading-default',

    // Chip – Attribute (Blue / Brand)
    'chip-attribute-default',
    'chip-attribute-hover',
    'chip-attribute-pressed',
    'chip-attribute-icon',
    'chip-attribute-active-border',

    // Chip – Measure (Green)
    'chip-measure-default',
    'chip-measure-hover',
    'chip-measure-pressed',
    'chip-measure-icon',
    'chip-measure-active-border',

    // Chip – Filter (Gray)
    'chip-filter-default',
    'chip-filter-hover',
    'chip-filter-pressed',
    'chip-filter-icon',
    'chip-filter-active-border',

    // Chip – Date (Purple)
    'chip-date-default',
    'chip-date-hover',
    'chip-date-pressed',
    'chip-date-icon',
    'chip-date-active-border',

    // Chip – Input
    'chip-input-default',
    'chip-input-hover',
    'chip-input-pressed',
    'chip-input-selected-default',
    'chip-input-selected-hover',
    'chip-input-selected-pressed',

    // Chip – Error
    'chip-error-default',
    'chip-error-hover',
    'chip-error-active-border',

    // Toggle
    'toggle-track-off',
    'toggle-track-on',
    'toggle-thumb-off',
    'toggle-thumb-on',
    'toggle-thumb-outline-off',
] as const;

// =========================================================================
// CSS variable prefixes — used when injecting / clearing tokens on :root
// =========================================================================

export const SYS_TOKEN_PREFIX = '--rd-sys-color-';
export const COMP_TOKEN_PREFIX = '--rd-comp-color-';

// =========================================================================
// Derived types
// =========================================================================

export type SystemTokenName = typeof SYSTEM_TOKEN_NAMES[number];
export type ComponentTokenName = typeof COMPONENT_TOKEN_NAMES[number];
